import React from 'react';
import { uniq } from 'lodash';

import { SearchQuery, SearchQueryModelConverter } from '@neptune/search-query-domain';
import { getEntityName } from '@neptune/shared/entity-domain';

import { KnownAttributes } from 'domain/experiment/attribute';

import { Leaderboard } from './search/leaderboard';
import { LeaderboardQueryNameAliases, searchLeaderboard } from './search/search-leaderboard';

export function useSearchLeaderboardNameDataSource({
  projectIdentifier,
  experimentsOnly,
  queryNameAliases,
  limit = 50,
  getAlias,
}: {
  projectIdentifier: string;
  experimentsOnly: boolean;
  queryNameAliases?: LeaderboardQueryNameAliases;
  limit?: number;
  getAlias?: (experimentName: string, runId: string) => string;
}) {
  return React.useCallback(
    async (searchQuery: SearchQuery) => {
      const nonArchivedQuery: SearchQuery = {
        criteria: [
          {
            attribute: KnownAttributes.Archived,
            operator: '=',
            type: 'bool',
            value: false,
          },
        ],
        operator: 'and',
      };

      const finalQuery = SearchQueryModelConverter.convertSearchQueryToNql({
        criteria: [nonArchivedQuery, searchQuery],
        operator: 'and',
      });

      const leaderboard = await searchLeaderboard({
        queryNameAliases,
        projectIdentifier,
        query: finalQuery,
        type: ['run'],
        sorting: {
          dir: 'descending',
          sortBy: {
            name: 'sys/creation_time',
            type: 'datetime',
            aggregationMode: 'auto',
          },
        },
        pagination: {
          offset: 0,
          limit,
        },
        attributesToFetch: ['sys/name'],
        experimentsOnly,
      });

      return getLeaderboardRunNames(leaderboard, getAlias);
    },
    [queryNameAliases, projectIdentifier, limit, experimentsOnly, getAlias],
  );
}

function getLeaderboardRunNames(
  { entries }: Leaderboard,
  getAlias?: (experimentName: string, runId: string) => string,
) {
  return uniq(
    entries
      .map((entry) => {
        const experimentName = getEntityName(entry);
        const runId = entry.id;
        return getAlias?.(experimentName, runId) || experimentName;
      })
      .filter(Boolean),
  );
}
